/**
 * Widget-proxy fetch helper.
 *
 * Every embed surface hits `${appBaseUrl}/api/widget-proxy/...` with the same
 * `X-Bolder-Widget` + `X-Bolder-Widget-Origin` headers, optional bearer JWT,
 * and JSON body. Centralised here so pages and providers don't each hand-roll
 * the `fetch` boilerplate (and so the next round of header tweaks happens in
 * exactly one place).
 */

export function getWidgetHeaders(token?: string | null): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Bolder-Widget': 'true',
    'X-Bolder-Widget-Origin': typeof window !== 'undefined' ? window.location.origin : '',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

interface WidgetProxyOptions {
  token?: string | null;
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
}

export async function widgetProxyFetch<T = unknown>(
  appBaseUrl: string,
  path: string,
  { token, method = 'GET', body, signal }: WidgetProxyOptions = {},
): Promise<T | null> {
  const url = `${appBaseUrl}/api/widget-proxy${path.startsWith('/') ? path : `/${path}`}`;
  try {
    const response = await fetch(url, {
      method,
      headers: getWidgetHeaders(token),
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal,
    });
    if (!response.ok) return null;
    return (await response.json()) as T;
  } catch (error) {
    // CORS / DNS / abort — match the per-helper try/catch behaviour the callers
    // used to have before they were folded into this helper.
    console.error('[widgetProxy] fetch failed:', path, error);
    return null;
  }
}
